import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from '@emotion/styled';
import SmallModalLayout from '../structure/SmallModalLayout';
import Button from '../general/Button';
import CustomScrollbars from '../structure/CustomScrollbars';
import useWindowSize from '../../hooks/UseWindowSize';
import { style_border_default } from '../../constants/stylesValues';
import { FoodOrderingFeatureFlagId, HealthcareSchedulingFeatureFlagId } from '../../constants/featureFlagIds';
import ApplicationModel from '../../models/applications/api/ApplicationModel';
import {
    color_colors_growth,
    color_colors_decline,
    color_text_light,
    color_text_default,
    color_shades_light,
    color_shades_lighter,
    color_shades_darker,
    color_defaults_white
} from '../../constants/colors';
import ModalHeader from '../general/ModalHeader';
import FooterBar from '../structure/FooterBar';
import { css } from '@emotion/css';
import { FOOD_ORDERING_TAGS, HEALTHCARE_TAGS } from '../../constants/analyticsConstants/generativeCallReview';

interface GraphFilterModalProps {
    isVisible: boolean;
    onClose: () => void;
    categoriesForDisplay: string[]
    tagsForDisplay: string[];
    categories: string[];
    tags: string[];
    onToggleTag: (tag: string) => void;
    onToggleCategory: (cat: string) => void;
    onApply: () => void | Promise<void>;
    isLoading?: boolean;
    application?: ApplicationModel;
    assistantIndustry?: string
    currentToggledTags?: string[],
    currentToggledCategories?: string[]
}

const MODAL_SCROLLBAR_SUBTRACTION: number = 175;

export const GraphFilterModal: React.FC<GraphFilterModalProps> = React.memo(({
    isVisible,
    onClose,
    categoriesForDisplay,
    tagsForDisplay: originalTagsForDisplay,
    categories,
    tags,
    onToggleTag,
    onToggleCategory,
    onApply,
    isLoading = false,
    application,
    assistantIndustry,
    currentToggledCategories,
    currentToggledTags
}) => {
    const TAGS_TO_FILTER_OUT = []

    const [localSelectedTags, setLocalSelectedTags] = useState<Set<string>>(new Set(currentToggledTags));
    const [localSelectedCategories, setLocalCategories] = useState<Set<string>>(new Set(currentToggledCategories));
    const [currentPage, setCurrentPage] = useState(1);
    const [isApplying, setIsApplying] = useState(false);
    const windowSize = useWindowSize();

    const TAGS_PER_PAGE = 50; // Show 50 tags per page for better performance
    const CATS_PER_PAGE = 50;

    useEffect(() => {
        if (isVisible) {
            setLocalCategories(new Set(currentToggledCategories));
            setLocalSelectedTags(new Set(currentToggledTags));
            setCurrentPage(1);
        }
    }, [isVisible, tags, categories]);

    const handleTagsToggle = useCallback((tag: string) => {
        onToggleTag(tag);
        setLocalSelectedTags(prev => {
            const newSet = new Set(prev);
            if (newSet.has(tag)) {
                newSet.delete(tag);
            } else {
                newSet.add(tag);
            }
            return newSet;
        });
    }, []);

    const handleCategoriesToggle = useCallback((cat: string) => {
        onToggleCategory(cat);
        setLocalCategories(prev => {
            const newSet = new Set(prev);
            if (newSet.has(cat)) {
                newSet.delete(cat);
            } else {
                newSet.add(cat);
            }
            return newSet;
        });
    }, []);

    const filteredTagPairs = useMemo(() => {
        let pairs = tags
            .map((tag, idx) => ({
                tag,
                tagDisplay: originalTagsForDisplay[idx],
            }));

        pairs = pairs.filter(({ tagDisplay }) => !TAGS_TO_FILTER_OUT.includes(tagDisplay));

        // Filter based on application type
        if (assistantIndustry === "FOOD_ORDERING") {
            // For foodOrdering, exclude healthcare-only tags
            pairs = pairs.filter(({ tag }) => FOOD_ORDERING_TAGS.includes(tag));
        } else if (assistantIndustry === "HEALTHCARE") {
            // For healthcare, exclude foodOrdering-only tags
            pairs = pairs.filter(({ tag }) => HEALTHCARE_TAGS.includes(tag));
        }

        return pairs;
    }, [tags, originalTagsForDisplay, application?.featureFlags]);

    const filteredCategoryPairs = useMemo(() => {
        let pairs = categories
            .map((cat, idx) => ({
                cat,
                catDisplay: categoriesForDisplay[idx],
            }));

        return pairs;
    }, [categories, categoriesForDisplay, application?.featureFlags]);

    const sortedTags = useMemo(() => {
        const tagPairs = filteredTagPairs.map(({ tag, tagDisplay }) => ({
            tag,
            tagDisplay,
            sortKey: (tagDisplay || tag).toLowerCase()
        }));
        return tagPairs.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
    }, [filteredTagPairs]);

    const paginationTagInfo = useMemo(() => {
        const totalPages = Math.ceil(sortedTags.length / TAGS_PER_PAGE);
        const startIndex = (currentPage - 1) * TAGS_PER_PAGE;
        const endIndex = Math.min(startIndex + TAGS_PER_PAGE, sortedTags.length);
        const currentPageTags = sortedTags.slice(startIndex, endIndex);

        return {
            totalPages,
            currentPageTags,
            totalItems: sortedTags.length,
            startIndex: startIndex + 1,
            endIndex
        };
    }, [sortedTags, currentPage]);

    const sortedCategories = useMemo(() => {
        const categoryPairs = filteredCategoryPairs.map(({ cat, catDisplay }) => ({
            cat,
            catDisplay,
            sortKey: (catDisplay || cat).toLowerCase()
        }));
        return categoryPairs.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
    }, [filteredCategoryPairs]);

    const paginationCatInfo = useMemo(() => {
        const totalPages = Math.ceil(sortedCategories.length / CATS_PER_PAGE);
        const startIndex = (currentPage - 1) * CATS_PER_PAGE;
        const endIndex = Math.min(startIndex + CATS_PER_PAGE, sortedCategories.length);
        const currentPageTags = sortedCategories.slice(startIndex, endIndex);

        return {
            totalPages,
            currentPageTags,
            totalItems: sortedCategories.length,
            startIndex: startIndex + 1,
            endIndex
        };
    }, [sortedCategories, currentPage]);

    const handleApply = useCallback(async () => {
        setIsApplying(true);

        try {
            await onApply();
            onClose();
        } catch (error) {
            console.error('Error applying tag filters:', error);
        } finally {
            setIsApplying(false);
        }
    }, [localSelectedTags, localSelectedCategories, tags, categories, onToggleTag, onToggleCategory, onApply, onClose]);

    const handleReset = useCallback(() => {
        for (const tag of localSelectedTags) {
            onToggleTag(tag);
        }
        for (const cat of localSelectedCategories) {
            onToggleCategory(cat);
        }
        setLocalSelectedTags(new Set());
        setLocalCategories(new Set());
    }, []);

    const hasChanges = useMemo(() => {
        const selectedEqual = localSelectedTags.size === tags.length &&
            Array.from(localSelectedTags).every(tag => tag in tags);
        const excludedEqual = localSelectedCategories.size === categories.length &&
            Array.from(localSelectedCategories).every(cat => cat in categories);
        return !selectedEqual || !excludedEqual;
    }, [localSelectedTags, localSelectedCategories, tags, categories]);

    return (
        <SmallModalLayout
            isVisible={isVisible}
            onClose={onClose}
            className={modalContentStyle}
            innerClassName={modalInnerStyle}
        >
            <ModalHeader title="Customize Graph Metrics" onClose={onClose} />
            <ModalBody>
                {isLoading || isApplying ? (
                    <LoadingContainer>
                        <LoadingSpinner />
                        <LoadingText>{isApplying ? 'Applying filters...' : 'Loading tags...'}</LoadingText>
                    </LoadingContainer>
                ) : (
                    <ScrollContainer>
                        <CustomScrollbars
                            autoHide
                            autoHeight
                            autoHeightMax={(windowSize.windowSize.innerHeight * .6) - MODAL_SCROLLBAR_SUBTRACTION}
                            style={{ width: '100%' }}
                        >
                            <FilterSections>

                                <ExcludeFilterSection>
                                    <SectionHeader>
                                        <SectionTitle>
                                            Categories
                                        </SectionTitle>
                                        <SectionSubtitle>Include these categories in the graph metrics</SectionSubtitle>
                                    </SectionHeader>
                                    <TagsList position='right'>
                                        {paginationCatInfo.currentPageTags.length === 0 ? (
                                            <NoTagsMessage>No tags available.</NoTagsMessage>
                                        ) : (
                                            paginationCatInfo.currentPageTags.map(({ cat, catDisplay }) => {
                                                const isSelected = localSelectedCategories.has(cat);
                                                return (
                                                    <TagCheckboxItem
                                                        key={`${cat}`}
                                                        onClick={() => handleCategoriesToggle(cat)}
                                                        disabled={false}
                                                        selected={isSelected}
                                                    >
                                                        <Checkbox
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            disabled={false}
                                                            readOnly
                                                        />
                                                        <TagText selected={isSelected} disabled={false}>
                                                            {catDisplay}
                                                        </TagText>
                                                    </TagCheckboxItem>
                                                );
                                            })
                                        )}
                                    </TagsList>
                                </ExcludeFilterSection>
                                <IncludeFilterSection>
                                    <SectionHeader>
                                        <SectionTitle>
                                            Tags
                                        </SectionTitle>
                                        <SectionSubtitle>Include these tags in the metrics graph</SectionSubtitle>
                                    </SectionHeader>
                                    <TagsList position='left'>
                                        {paginationTagInfo.currentPageTags.length === 0 ? (
                                            <NoTagsMessage>No tags available.</NoTagsMessage>
                                        ) : (
                                            paginationTagInfo.currentPageTags.map(({ tag, tagDisplay }) => {
                                                const isSelected = localSelectedTags.has(tag);
                                                return (
                                                    <TagCheckboxItem
                                                        key={`${tag}`}
                                                        onClick={() => handleTagsToggle(tag)}
                                                        disabled={false}
                                                        selected={isSelected}
                                                    >
                                                        <Checkbox
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            disabled={false}
                                                            readOnly
                                                        />
                                                        <TagText selected={isSelected} disabled={false}>
                                                            {tagDisplay}
                                                        </TagText>
                                                    </TagCheckboxItem>
                                                );
                                            })
                                        )}
                                    </TagsList>
                                </IncludeFilterSection>
                            </FilterSections>

                            {paginationTagInfo.totalItems > TAGS_PER_PAGE && (
                                <PaginationFooter>
                                    <PaginationControls>
                                        <PaginationButton
                                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                            disabled={currentPage === 1}
                                        >
                                            Previous
                                        </PaginationButton>
                                        <PageInfo>
                                            {currentPage} / {paginationTagInfo.totalPages}
                                        </PageInfo>
                                        <PaginationButton
                                            onClick={() => setCurrentPage(p => Math.min(paginationTagInfo.totalPages, p + 1))}
                                            disabled={currentPage === paginationTagInfo.totalPages}
                                        >
                                            Next
                                        </PaginationButton>
                                    </PaginationControls>
                                </PaginationFooter>
                            )}
                        </CustomScrollbars>
                    </ScrollContainer>
                )}
            </ModalBody>
            <FooterBar>
                <ButtonsWrapper>

                    <ResetButton
                        themes={['white-small']}
                        text="Reset All"
                        onClick={handleReset}
                        disabled={(localSelectedTags.size === 0 && localSelectedCategories.size === 0) || isApplying}
                    />
                    <CancelAndApplyButtons>
                        <ApplyButton
                            themes={['primary']}
                            text="Apply Filters"
                            onClick={handleApply}
                            disabled={!hasChanges || isApplying}
                            loading={isApplying}
                        />
                    </CancelAndApplyButtons>

                </ButtonsWrapper>
            </FooterBar>
        </SmallModalLayout>
    );
});


const ButtonsWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
`;

const modalContentStyle = css`
    width: 100%;
    max-width: 900px;
    max-height: 80vh;

    @media (max-width: 768px) {
        width: 98%;
        max-width: 100%;
        max-height: 90vh;
    }
`;

const CancelAndApplyButtons = styled.div``;

const modalInnerStyle = css`
    display: flex;
    flex-direction: column;
    height: 100%;
`;

const ModalBody = styled.div`
    padding: 4px 4px 24px 24px;
    padding-right: 4px;
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    position: relative;
`;

const ScrollContainer = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    position: relative;
    margin-right: -20px; /* Extend container to the right */
    padding-right: 24px; /* Add more padding to push content away from scrollbar */
    
    /* Position scrollbar track far outside the content area */
    .scrollbar-track-y {
        right: -20px;
        left: auto;
        width: 16px;
        background: transparent;
        z-index: 1000;
    }
    
    /* Position scrollbar thumb */
    .scrollbar-thumb-y {
        right: -18px;
        width: 12px;
        background: ${color_shades_darker};
        border-radius: 6px;
    }
    
    /* Ensure content doesn't overlap with scrollbar */
    .scrollbar-container {
        margin-right: 0;
        padding-right: 0;
    }
    
    /* Override any default scrollbar positioning for SimpleBar */
    .simplebar-track.simplebar-vertical {
        right: -20px;
        width: 16px;
        z-index: 1000;
    }
    
    .simplebar-scrollbar:before {
        background: ${color_shades_darker};
        width: 12px;
        right: 2px;
        border-radius: 6px;
    }
    
    /* For react-custom-scrollbars */
    .scrollbar-track-vertical {
        right: -20px;
        width: 16px;
        background: transparent;
    }
    
    .scrollbar-thumb-vertical {
        background: ${color_shades_darker};
        border-radius: 6px;
        width: 12px;
    }
`;

const FilterSections = styled.div`
    display: flex;
    flex-direction: row;
    flex: 1;

    @media (max-width: 768px) {
        flex-direction: column;
    }
`;

const IncludeFilterSection = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
`;

const ExcludeFilterSection = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    margin-right: 18px;
`;

const SectionHeader = styled.div`
    margin-bottom: 12px;
    flex-shrink: 0;
`;

const SectionTitle = styled.h3`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 1rem;
    font-weight: 600;
    color: ${color_text_default};
    margin: 0 0 4px 0;
`;

const SectionSubtitle = styled.p`
    font-size: 0.75rem;
    color: ${color_text_light};
    margin: 0;
    font-style: italic;
`;

const IncludeIcon = styled.span`
    color: ${color_colors_growth};
    font-weight: bold;
    font-size: 1rem;
`;

const ExcludeIcon = styled.span`
    color: ${color_colors_decline};
    font-weight: bold;
    font-size: 1rem;
`;

const TagCheckboxItem = styled.div<{ selected: boolean; disabled: boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
    border-radius: 6px;
    margin-bottom: 2px;
    background: ${props => {
        if (props.disabled) return color_shades_light;
        if (props.selected) return color_shades_lighter;
        return 'transparent';
    }};
    border: 1px solid transparent;
    opacity: ${props => props.disabled ? '0.5' : '1'};
    
    &:hover {
        background: ${props => {
        if (props.disabled) return color_shades_light;
        if (props.selected) return color_shades_lighter;
        return color_shades_lighter;
    }};
    }

    &:last-child {
        margin-bottom: 0;
    }
`;

const Checkbox = styled.input<{ disabled?: boolean }>`
    width: 16px;
    height: 16px;
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
    margin: 0;
    flex-shrink: 0;
`;

const LoadingContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    gap: 16px;
`;

const LoadingSpinner = styled.div`
    width: 40px;
    height: 40px;
    border: 3px solid ${color_shades_light};
    border-top: 3px solid ${color_colors_growth};
    border-radius: 50%;
    animation: spin 1s linear infinite;

    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }
`;

const LoadingText = styled.div`
    font-size: 0.875rem;
    color: ${color_text_light};
`;

const TagsList = styled.div<{ position?: 'left' | 'right' }>`
    flex: 1;
    border: ${style_border_default};
    border-radius: 8px;
    padding: 8px;
    background: ${color_defaults_white};
    min-height: 200px;
    margin-right: ${props => props.position === 'left' ? '4px' : '0'};
    margin-left: ${props => props.position === 'right' ? '4px' : '0'};

    @media (max-width: 768px) {
        min-height: 150px;
    }
`;

const NoTagsMessage = styled.div`
    text-align: center;
    padding: 32px 16px;
    color: ${color_text_light};
    font-size: 0.875rem;
    font-style: italic;
`;

const TagText = styled.span<{ selected: boolean; disabled: boolean }>`
    font-size: 0.9rem;
    color: ${props => {
        if (props.disabled) return color_text_light;
        if (props.selected) return color_text_default;
        return color_text_default;
    }};
    font-weight: ${props => props.selected ? '500' : 'normal'};
    flex: 1;
`;

const ResetButton = styled(Button)`
    min-width: 80px;
`;

const CancelButton = styled(Button)`
    min-width: 80px;
`;

const ApplyButton = styled(Button)`
    min-width: 120px;
`;

const PaginationFooter = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 8px 0;
    border-top: ${style_border_default};
    margin-top: 8px;
    flex-shrink: 0;
`;

const PaginationControls = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const PaginationButton = styled.button`
    padding: 4px 8px;
    border: 1px solid ${color_shades_darker};
    background: ${color_defaults_white};
    color: ${color_text_default};
    font-size: 0.75rem;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
        background: ${color_shades_lighter};
        border-color: ${color_shades_darker};
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        background: ${color_shades_light};
    }
`;

const PageInfo = styled.span`
    font-size: 0.75rem;
    color: ${color_text_default};
    font-weight: 500;
    margin: 0 4px;
`;
